import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { StatusBadge } from "../components/StatusBadge";
import { Plus, Printer, FileText } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatIDR, formatApiError } from "../lib/format";
import { DIVISIONS, WAGE_STATUS_LABEL } from "../lib/constants";

export default function Wages() {
  const [rows, setRows] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [form, setForm] = useState({ booking_id: "", division_id: "", staff_name: "", role: "", job_date: "", base_fee: 0, additional_fee: 0, deduction: 0, notes: "" });
  const [payForm, setPayForm] = useState({ payment_date: new Date().toISOString().slice(0,10), method: "Transfer", notes: "" });

  const load = () => Promise.all([
    api.get("/job-wages").then(({ data }) => setRows(data)),
    api.get("/bookings").then(({ data }) => setBookings(data)),
  ]);
  useEffect(() => { load(); }, []);

  const totalSelected = useMemo(() =>
    rows.filter((r) => selected.has(r.id)).reduce((a, r) => a + r.final_amount, 0), [rows, selected]);

  const submit = async () => {
    try {
      await api.post("/job-wages", { ...form, base_fee: Number(form.base_fee), additional_fee: Number(form.additional_fee), deduction: Number(form.deduction) });
      toast.success("Fee crew ditambahkan"); setOpen(false); load();
      setForm({ booking_id: "", division_id: "", staff_name: "", role: "", job_date: "", base_fee: 0, additional_fee: 0, deduction: 0, notes: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const payBatch = async () => {
    try {
      const { data } = await api.post("/job-wages/pay-batch", { wage_ids: Array.from(selected), ...payForm });
      toast.success("Batch dibayar: " + data.wage_payment_code);
      setPayOpen(false); setSelected(new Set()); load();
      window.open(`/print/wage-payment/${data.id}`, "_blank");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  return (
    <div className="space-y-6" data-testid="wages-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Fee Crew / Job Wages</h1>
          <p className="text-sm text-[#5C6066] mt-1">Kelola honor crew per acara. Nomor pembayaran: <span className="font-mono">WGP-DWG-YYYY-XXXX</span></p>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <>
              <Button variant="outline" onClick={() => {
                const ids = Array.from(selected).join(",");
                window.open(`/print/bulk-wages?ids=${ids}&layout=2`, "_blank");
              }} data-testid="btn-bulk-print-slips">
                <FileText className="w-4 h-4 mr-1.5" /> Cetak Slip ({selected.size})
              </Button>
              <Button onClick={() => setPayOpen(true)} className="bg-brand text-white" data-testid="btn-pay-batch">
                Bayar {selected.size} — {formatIDR(totalSelected)}
              </Button>
            </>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="btn-add-wage"><Plus className="w-4 h-4 mr-1.5" /> Fee Crew Baru</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Tambah Fee Crew</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div><Label>Pemesanan</Label>
                  <Select value={form.booking_id} onValueChange={(v) => {
                    const b = bookings.find(x => x.id === v);
                    setForm({ ...form, booking_id: v, job_date: b?.event_date || form.job_date });
                  }}>
                    <SelectTrigger className="mt-1.5"><SelectValue placeholder="Pilih pemesanan" /></SelectTrigger>
                    <SelectContent>{bookings.map((b) => <SelectItem key={b.id} value={b.id}>{b.booking_code} — {b.event_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Divisi</Label>
                  <Select value={form.division_id} onValueChange={(v) => setForm({ ...form, division_id: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{DIVISIONS.map((d) => <SelectItem key={d.id} value={d.id}>{d.short}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Nama Crew</Label><Input className="mt-1.5" value={form.staff_name} onChange={(e) => setForm({ ...form, staff_name: e.target.value })} /></div>
                  <div><Label>Role</Label><Input className="mt-1.5" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Leader, PIC, MUA, ..." /></div>
                </div>
                <div><Label>Tanggal Job</Label><Input type="date" className="mt-1.5" value={form.job_date} onChange={(e) => setForm({ ...form, job_date: e.target.value })} /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Fee</Label><Input type="number" className="mt-1.5" value={form.base_fee} onChange={(e) => setForm({ ...form, base_fee: e.target.value })} /></div>
                  <div><Label>Tambahan</Label><Input type="number" className="mt-1.5" value={form.additional_fee} onChange={(e) => setForm({ ...form, additional_fee: e.target.value })} /></div>
                  <div><Label>Potongan</Label><Input type="number" className="mt-1.5" value={form.deduction} onChange={(e) => setForm({ ...form, deduction: e.target.value })} /></div>
                </div>
                <div><Label>Catatan</Label><Textarea rows={2} className="mt-1.5" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
                <Button className="bg-brand text-white" onClick={submit} disabled={!form.booking_id || !form.staff_name}>Simpan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                <th className="w-10 px-3 py-3"></th>
                <th className="text-left px-4 py-3">Crew / Role</th>
                <th className="text-left px-4 py-3">Pemesanan</th>
                <th className="text-left px-4 py-3">Tanggal</th>
                <th className="text-right px-4 py-3">Fee Akhir</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8C9096]">Belum ada fee crew.</td></tr>}
              {rows.map((w) => (
                <tr key={w.id} className="border-t border-[#F0EBE0] hover:bg-[#F7F4EE]" data-testid={`wage-${w.id}`}>
                  <td className="px-3 py-3">
                    {w.status !== "Paid" && (
                      <Checkbox checked={selected.has(w.id)} onCheckedChange={() => toggle(w.id)} data-testid={`wage-check-${w.id}`} />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{w.staff_name}</div>
                    <div className="text-[11px] text-[#8C9096]">{w.role}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs">{w.booking_code}</div>
                    <div className="text-[11px] text-[#8C9096]">{w.event_name}</div>
                  </td>
                  <td className="px-4 py-3">{formatDate(w.job_date)}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatIDR(w.final_amount)}</td>
                  <td className="px-4 py-3"><StatusBadge status={w.status} label={WAGE_STATUS_LABEL[w.status]} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bayar Fee Crew (Batch)</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="p-3 rounded bg-[#FAF6EE] border border-[#E5E0D8]">
              <div className="text-xs text-[#8C9096]">Total {selected.size} crew</div>
              <div className="font-display text-xl font-bold">{formatIDR(totalSelected)}</div>
            </div>
            <div><Label>Tanggal Bayar</Label><Input type="date" className="mt-1.5" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} /></div>
            <div><Label>Metode</Label><Input className="mt-1.5" value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} /></div>
            <div><Label>Catatan</Label><Textarea rows={2} className="mt-1.5" value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Batal</Button>
            <Button className="bg-brand text-white" onClick={payBatch} data-testid="btn-confirm-pay-batch">Bayar & Cetak Slip</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
